import React, { useCallback, useEffect, useRef, useState } from 'react';
import { announceMathMenu, MathFormulaMenu } from '../components/MathFormulaMenu';
import { ATOMIC_PILL_SELECTOR } from '../utils/atomicCaret';
import {
  placeCaretAfterAtomic,
  pinCaretAfterPillIndex,
  schedulePlaceCaretAfterAtomic,
} from '../utils/atomicCaret';
import { focusEditable } from '../utils/focusUtils';
import {
  buildMathHtml,
  refreshVariableHtml,
  replaceTriggerWithHtml,
} from '../utils/variableUtils';

/**
 * Insert / edit inline math pills (same chrome as variable pills).
 */
export const useMathPills = ({
  blockId,
  contentEditableRef,
  contentRef,
  setContent,
  onUpdate,
  resolveVariable,
  availableVariables = [],
  onCreateVariable = null,
}) => {
  const [menu, setMenu] = useState(null);
  const textNodeRef = useRef(null);
  const triggerRef = useRef({ trigger: '/', queryLength: 0 });
  const editingPillRef = useRef(null);

  const closeMenu = useCallback(
    ({ restoreFocus: doRestore = true } = {}) => {
      setMenu(null);
      editingPillRef.current = null;
      textNodeRef.current = null;
      if (!doRestore) return;
      if (blockId) focusEditable(blockId, { preventScroll: true });
    },
    [blockId]
  );

  const commitHtml = useCallback(
    (html) => {
      if (contentRef) contentRef.current = html;
      setContent?.(html);
      onUpdate?.(blockId, { content: html });
    },
    [blockId, contentRef, onUpdate, setContent]
  );

  const insertMathPill = useCallback(
    (expr) => {
      const el = contentEditableRef?.current;
      if (!el) {
        closeMenu({ restoreFocus: true });
        return;
      }
      const pillHtml = refreshVariableHtml(buildMathHtml(expr), resolveVariable);
      const { trigger, queryLength } = triggerRef.current;
      const placed = replaceTriggerWithHtml(
        el,
        textNodeRef.current,
        trigger,
        queryLength,
        pillHtml
      );
      if (!placed) {
        el.innerHTML = `${el.innerHTML}${pillHtml}`;
        el.focus({ preventScroll: true });
        const pill = el.querySelector('[data-doc-math]:last-of-type');
        if (pill) placeCaretAfterAtomic(pill);
      }
      const pills = el.querySelectorAll(ATOMIC_PILL_SELECTOR);
      const pillIndex = pills.length - 1;
      commitHtml(el.innerHTML);
      if (pillIndex >= 0) {
        pinCaretAfterPillIndex(el, pillIndex);
        schedulePlaceCaretAfterAtomic(
          () => contentEditableRef?.current,
          (host) => host?.querySelectorAll(ATOMIC_PILL_SELECTOR)[pillIndex]
        );
      }
      closeMenu({ restoreFocus: false });
    },
    [closeMenu, commitHtml, contentEditableRef, resolveVariable]
  );

  const saveEditedPill = useCallback(
    (expr) => {
      const pill = editingPillRef.current;
      const el = contentEditableRef?.current;
      if (!pill || !el?.contains(pill)) {
        insertMathPill(expr);
        return;
      }
      pill.setAttribute('data-math-expr', expr);
      commitHtml(refreshVariableHtml(el.innerHTML, resolveVariable));
      closeMenu({ restoreFocus: false });
      requestAnimationFrame(() => placeCaretAfterAtomic(pill));
    },
    [closeMenu, commitHtml, contentEditableRef, insertMathPill, resolveVariable]
  );

  const openFromSlash = useCallback((position, { textNode, queryLength }) => {
    announceMathMenu();
    textNodeRef.current = textNode || null;
    triggerRef.current = { trigger: '/', queryLength: queryLength || 0 };
    editingPillRef.current = null;
    setMenu({ position, initialExpr: '', key: Date.now() });
  }, []);

  const openForEdit = useCallback((pill, position) => {
    announceMathMenu();
    editingPillRef.current = pill;
    textNodeRef.current = null;
    setMenu({
      position,
      initialExpr: pill.getAttribute('data-math-expr') || '',
      key: Date.now(),
    });
  }, []);

  useEffect(() => {
    // Press, not click. A click on a contenteditable=false formula often never
    // fires, so the editor looked impossible to reopen.
    const onPointerDown = (e) => {
      if (e.button !== 0) return;
      const pill = e.target?.closest?.('[data-doc-math]');
      if (!pill || pill.closest('.doc-math-menu')) return;
      const el = contentEditableRef?.current;
      if (!el || !el.contains(pill)) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = pill.getBoundingClientRect();
      openForEdit(pill, { x: rect.left, y: rect.bottom + 4, caretTop: rect.top });
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [contentEditableRef, openForEdit]);

  const mathMenu = menu ? (
    <MathFormulaMenu
      key={menu.key}
      position={menu.position}
      initialExpr={menu.initialExpr}
      resolveVariable={resolveVariable}
      availableVariables={availableVariables}
      onCreateVariable={onCreateVariable}
      onSubmit={(expr) => {
        if (editingPillRef.current) saveEditedPill(expr);
        else insertMathPill(expr);
      }}
      onClose={closeMenu}
    />
  ) : null;

  return { mathMenu, openFromSlash, openForEdit };
};
